import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DeactivatableButton,
  Form,
  FormSelectInput,
  FormTextInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  unexpectedPolicyStatusConflictMessage,
  useToast,
} from '@folio/components';
import { useAccessibleCustomerIds } from '@folio/hooks';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import {
  customerControllerGetCustomerQueryOptions,
  invitationControllerInvite,
  keyValueFieldsToObject,
  relationsControllerDeleteRelation,
  viewSpecificControllerListCustomerRelationsQueryKey,
  viewSpecificControllerListCustomerRelationsQueryOptions,
  type ListCustomerRelationsOutput,
} from 'korint-sdk';
import { Loader2Icon, MailIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Stack } from '@folio/components/ui/flex';

export function PolicyholderInvitationModule() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { policy, workingBranch } = usePolicyOnDashboard();
  const accessibleCustomerIds = useAccessibleCustomerIds(policy.customers);

  const customers = useSuspenseQueries({
    queries: accessibleCustomerIds.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, {
        branchId: workingBranch?.id,
      }),
    })),
  });

  const customerByRole = useMemo(() => {
    const map = new Map<string, { customerId: string; contactEmail: string }>();
    for (const [index, customerId] of accessibleCustomerIds.entries()) {
      const customer = customers[index]?.data;
      if (customer) {
        const fields = keyValueFieldsToObject(customer.customFields ?? []);
        map.set(customer.role, {
          customerId,
          contactEmail: fields.contactEmail as string,
        });
      }
    }
    return map;
  }, [accessibleCustomerIds, customers]);

  const policyholderInvitationModuleConfig = usePolicyProductConfig(
    'policyholderInvitationModuleConfig',
  );

  // Get available relation types from config
  const relationTypes = policyholderInvitationModuleConfig.relationTypes || [];

  const relationsQueries = useSuspenseQueries({
    queries: accessibleCustomerIds.map(customerId => ({
      ...viewSpecificControllerListCustomerRelationsQueryOptions(customerId, {
        types: relationTypes,
      }),
      select: (data: ListCustomerRelationsOutput) => data.data.map(r => ({ ...r, customerId })),
    })),
  });
  const allRelations = relationsQueries.flatMap(q => q.data);

  const [invitationWasJustSent, setInvitationWasJustSent] = useState(false);
  const [relationToDelete, setRelationToDelete] = useState<{ id: string; email: string } | null>(
    null,
  );

  const defaultRole = relationTypes.length === 1 ? relationTypes[0] : '';
  const defaultEntry = customerByRole.get(defaultRole);

  // Form with role field
  const form = useForm({
    values: {
      email: (defaultEntry?.contactEmail as string) ?? '',
      role: defaultRole,
    },
    resolver: zodResolver(
      z.object({
        email: z
          .string({ required_error: t('policyholderInvitation.emailRequired') })
          .email({ message: t('policyholderInvitation.invalidEmail') }),
        role: z.string().min(1, t('policyholderInvitation.roleRequired')),
      }),
    ),
  });

  // Auto-select if single role
  useEffect(() => {
    if (relationTypes.length === 1) {
      form.setValue('role', relationTypes[0]);
    }
  }, [relationTypes, form]);

  // Update email when role changes
  const selectedRole = form.watch('role');
  useEffect(() => {
    const entry = customerByRole.get(selectedRole);
    if (entry?.contactEmail) {
      form.setValue('email', entry.contactEmail);
    }
  }, [selectedRole, customerByRole, form]);

  const queryClient = useQueryClient();

  const { mutateAsync: inviteUser, isPending } = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const entry = customerByRole.get(role);
      return invitationControllerInvite({
        email,
        customerId: entry!.customerId,
        policyId: policy.id,
        branchId: workingBranch?.id,
        role,
      });
    },
    onSuccess: () => {
      toast({ title: t('policyholderInvitation.invitationSent') });
      setInvitationWasJustSent(true);

      setTimeout(() => {
        setInvitationWasJustSent(false);

        form.setValue('email', '');
        for (const customerId of accessibleCustomerIds) {
          queryClient.invalidateQueries({
            queryKey: viewSpecificControllerListCustomerRelationsQueryKey(customerId),
          });
        }
      }, 5000);
    },
  });

  const { mutateAsync: deleteRelation, isPending: isDeleting } = useMutation({
    mutationFn: (relationId: string) => relationsControllerDeleteRelation(relationId),
    onSuccess: () => {
      for (const customerId of accessibleCustomerIds) {
        queryClient.invalidateQueries({
          queryKey: viewSpecificControllerListCustomerRelationsQueryKey(customerId),
        });
      }
      toast({ title: t('policyholderInvitation.accountUnlinked') });
      setRelationToDelete(null);
    },
  });

  if (!relationTypes || relationTypes.length === 0) {
    return null;
  }

  const showRoleColumn = relationTypes.length > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('policyholderInvitation.title')}</CardTitle>
      </CardHeader>
      <Stack asChild>
        <CardContent>
          {allRelations.length === 0 ? (
            <div>{t('policyholderInvitation.emptyState')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('policyholderInvitation.accountsHeader')}</TableHead>
                  {showRoleColumn && (
                    <TableHead>{t('policyholderInvitation.roleHeader')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allRelations.map(relation => (
                  <TableRow key={relation.userId}>
                    <TableCell>{relation.email}</TableCell>
                    {showRoleColumn && (
                      <TableCell>{t(`relationTypes.${relation.relationType}.label`)}</TableCell>
                    )}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setRelationToDelete({
                            id: relation.id,
                            email: relation.email,
                          })
                        }
                      >
                        <Trash2Icon className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Form {...form}>
            <form
              className="flex gap-2 items-end"
              onSubmit={form.handleSubmit(async ({ email, role }) => {
                await inviteUser({ email, role });
              })}
            >
              <FormTextInput
                placeholder="joe.doe@host.com"
                label={t('policyholderInvitation.emailLabel')}
                name="email"
                control={form.control}
                disabled={invitationWasJustSent}
                type="email"
              />

              {/* Only show role selector if multiple relation types */}
              {relationTypes.length > 1 && (
                <FormSelectInput
                  label={t('policyholderInvitation.roleFieldLabel')}
                  name="role"
                  control={form.control}
                  placeholder={t('policyholderInvitation.roleFieldPlaceholder')}
                  options={relationTypes.map(type => ({
                    label: t(`relationTypes.${type}.label`),
                    value: type,
                  }))}
                />
              )}

              <DeactivatableButton
                type="submit"
                variant="ghost"
                icon={<MailIcon />}
                isLoading={isPending}
                rules={[
                  {
                    isValid: customerByRole.has(selectedRole),
                    message: t('policyholderInvitation.noCustomerForRole'),
                  },
                  {
                    isValid: !invitationWasJustSent,
                    message: t('policyholderInvitation.waitBeforeReinvite'),
                  },
                  {
                    isValid: workingBranch?.branchStatus !== 'BRANCH_CLOSED',
                    message: t('finalizeNewBusiness.branchClosed'),
                  },
                  {
                    isValid: policy.status !== 'POLICY_SUSPENDED',
                    message: unexpectedPolicyStatusConflictMessage(['POLICY_SUSPENDED'], t),
                  },
                ]}
              >
                {t('policyholderInvitation.inviteButton')}
              </DeactivatableButton>
            </form>
          </Form>
        </CardContent>
      </Stack>

      <AlertDialog
        open={!!relationToDelete}
        onOpenChange={open => !open && setRelationToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('policyholderInvitation.confirmDelete.title', {
                email: relationToDelete?.email,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('policyholderInvitation.confirmDelete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('policyholderInvitation.confirmDelete.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                if (relationToDelete) deleteRelation(relationToDelete.id);
              }}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  {t('policyholderInvitation.confirmDelete.unlinking')}
                </>
              ) : (
                t('policyholderInvitation.confirmDelete.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
